/* eslint-disable */
import {
	InstancedMesh,
	Matrix4,
	Color,
	LineBasicMaterial,
	LineSegments
} from 'three';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Group, EdgesGeometry } from 'three';
import { getOptimizedBLKData } from '@/js/three/VOXUtils.js';
import conf from '@/js/conf/conf'
const cdnHost = conf.hosts().cdnHost
const OBJ_MODEL_CACHE = new Map();
const VOXEL_OPACITY = 0.9
class BLKMesh extends Group {

	constructor( chunk, currentZ = -1, edgeType = 'natural', modelType = 'blk01', objModels = null, optimize = 0, highLight = true, keepOrginColor = false ) {

		super();

		const data = chunk.data;
		const size = chunk.size;
		const palette = chunk.palette;
		const fixSide = 77.9999;
		const fixPos = (modelType === 'chain01' || modelType === 'chain02')?  0.0995 : 0.1257;
		const BLKData = getOptimizedBLKData(chunk, optimize);

		// 颗粒统计信息
		this.partStats = {
			total: 0,
			parts: []
		};
		
		const getBLKData = (x, y, z) => {
			if (BLKData && BLKData.data) {
				// 从BLKData中查找对应的体素数据
				const key = `${x},${y},${z}`;
				const voxelData = BLKData.data[key];
				if (voxelData) {
					const [c, adjacentCount, blkId, rotateX, rotateY, rotateZ, show, direction] = voxelData;
					// 如果show为false，则不渲染这个体素
					if (!show) {
						return null;
					}
					return { c, adjacentCount, blkId, rotateX, rotateY, rotateZ, show, direction};
				}
			}
			return null
		};

		// 获取对应blkId的OBJ模型
		const getObjForBlkId = (blkId) => {
			if (objModels && objModels[blkId]) {
				return objModels[blkId];
			}
			// 如果没有找到对应的模型，使用默认的obj
			return null;
		};


		// 获取渲染颜色（与渲染代码保持一致）
		const getRenderColor = (colorIndex) => {
			if (colorIndex === undefined || colorIndex === null || colorIndex < 0) {
				return '#ffffff';
			}
			
			if (!palette || !Array.isArray(palette) || colorIndex >= palette.length) {
				return '#ffffff';
			}
			
			const hex = palette[colorIndex];
			if (hex === undefined || hex === null) {
				return '#ffffff';
			}
			
			// 使用与渲染代码相同的RGB转换逻辑
			const r = (hex >> 0 & 0xff) / 0xff;
			const g = (hex >> 8 & 0xff) / 0xff;
			const b = (hex >> 16 & 0xff) / 0xff;
			
			// 转换为16进制颜色字符串
			const rHex = Math.round(r * 255).toString(16).padStart(2, '0');
			const gHex = Math.round(g * 255).toString(16).padStart(2, '0');
			const bHex = Math.round(b * 255).toString(16).padStart(2, '0');
			
			return `#${rHex}${gHex}${bHex}`;
		};

		const processObj = () => {
			const offsety = size.x;
			const offsetz = size.x * size.y;
			const array = new Uint8Array( size.x * size.y * size.z );
			for ( let j = 0; j < data.length; j += 4 ) {
				const x = data[ j + 0 ];
				const y = data[ j + 1 ];
				const z = data[ j + 2 ];
				const index = x + ( y * offsety ) + ( z * offsetz );
				array[ index ] = 255;
			}

			const voxels = [];
			const added = new Set(); // 防止重复添加
			
			// 颗粒统计
			const partCounts = {};
			const partColorCounts = {};

			// 判断是否显示完整模型
			const showFullModel = currentZ === -1 || currentZ > size.z - 1;

			for (let j = 0; j < data.length; j += 4) {
				const x = data[j + 0];
				const y = data[j + 1];
				const z = data[j + 2];
				
				// 如果显示完整模型，包含所有体素；否则只包含到currentZ层
				if (showFullModel || z <= currentZ) {
					const blkData = getBLKData(x, y, z);
					// 如果blkData为null，跳过这个体素
					if (blkData === null) continue;
					const { c, adjacentCount, blkId, rotateX, rotateY, rotateZ, show, direction } = blkData;
					const colorIndex = data[j + 3];
					if (!show) continue;
					// 统计颗粒数量 - 使用blkId而不是partName
					if (!partCounts[blkId]) {
						partCounts[blkId] = 0;
						partColorCounts[blkId] = {};
					}
					partCounts[blkId]++;
					
					// 统计颜色 - 使用与渲染代码一致的颜色处理逻辑
					const color = getRenderColor(colorIndex);
					if (!partColorCounts[blkId][color]) {
						partColorCounts[blkId][color] = 0;
					}
					partColorCounts[blkId][color]++;
					
					voxels.push({ 
						x, y, z, 
						c: colorIndex, 
						blkId: blkId, 
						rotateX: rotateX, 
						rotateY: rotateY, 
						rotateZ: rotateZ,
						adjacentCount: adjacentCount,
						direction: direction
					});
					added.add(`${x},${y},${z}`);
				}
			}

			// 更新颗粒统计信息
			this.partStats.total = voxels.length;
			this.partStats.parts = [];
			for (const [blkId, count] of Object.entries(partCounts)) {
				const colors = partColorCounts[blkId];
				this.partStats.parts.push({
					type: blkId, // 使用blkId而不是partName
					count: count,
					color: colors
				});
			}

			// 按blkId分组处理体素
			const voxelGroups = {};
			for (const voxel of voxels) {
				const blkId = voxel.blkId;
				if (!voxelGroups[blkId]) {
					voxelGroups[blkId] = [];
				}
				voxelGroups[blkId].push(voxel);
			}

			// 为每个blkId创建对应的InstancedMesh
			for (const [blkId, groupVoxels] of Object.entries(voxelGroups)) {
				const groupObj = getObjForBlkId(blkId);
				if (!groupObj) {
					continue;
				}
				// 调试：显示旋转信息
				const rotationStats = {};
				for (const voxel of groupVoxels) {
					const rotKey = `${voxel.rotateX},${voxel.rotateY},${voxel.rotateZ}`;
					rotationStats[rotKey] = (rotationStats[rotKey] || 0) + 1;
				}
				let groupBaseMesh = null;
				groupObj.traverse(child => {
					if (child.isMesh && !groupBaseMesh) groupBaseMesh = child;
				});
				if (!groupBaseMesh) continue;

				groupBaseMesh.geometry.computeBoundingBox();
				const groupBbox = groupBaseMesh.geometry.boundingBox;
				const groupMinSide = Math.min(
					groupBbox.max.x - groupBbox.min.x,
					groupBbox.max.y - groupBbox.min.y,
					groupBbox.max.z - groupBbox.min.z
				);
				let groupScale = 0.999 / (fixSide || groupMinSide);

				// 创建该组的InstancedMesh（拆分正常与半透明）
				const groupGeometry = groupBaseMesh.geometry;
				const buildInstanced = (list, faded) => {
					if (!list.length) return null;
					const mat = groupBaseMesh.material.clone();
					mat.vertexColors = false;
					if (faded) { mat.transparent = true; mat.opacity = VOXEL_OPACITY; }
					const im = new InstancedMesh(groupGeometry, mat, list.length);
					const color = new Color();
					const matrix = new Matrix4();
					for (let i = 0; i < list.length; i++) {
						const voxel = list[i];
						let px = voxel.x - size.x / 2 + 0.5;
						let py = voxel.z - size.z / 2 + 0.5;
						let pz = -voxel.y + size.y / 2 + 0.5;
						matrix.identity();
						matrix.scale({x: groupScale, y: groupScale, z: groupScale});
						if (voxel.rotateX !== 0) {
							matrix.multiply(new Matrix4().makeRotationX(voxel.rotateX * Math.PI / 180));
						}
						if (voxel.rotateY !== 0) {
							if (voxel.blkId === '4X' && voxel.direction === 'Y'){
								matrix.multiply(new Matrix4().makeRotationY(90 * Math.PI / 180));
							} else {
								matrix.multiply(new Matrix4().makeRotationY(voxel.rotateY * Math.PI / 180));
							}
							if (voxel.rotateY === 360) {
								pz = -voxel.y + size.y / 2 + 0.5 + fixPos;
								if (voxel.blkId === '4X' && voxel.direction === 'Y') {
									px = voxel.x - size.x / 2 + 1.5 + 2 * fixPos;
								}
							} else if (voxel.rotateY === 270) {
								px = voxel.x - size.x / 2 + 0.5 + fixPos;
								pz = -voxel.y + size.y / 2 - 0.5 - fixPos;
								if (voxel.blkId === '3X') {
									pz = -voxel.y + size.y / 2 - 1.5 - fixPos;
								}
							} else if (voxel.rotateY === 180) {
								px = voxel.x - size.x / 2 + 1.5 + 2 * fixPos;
								pz = -voxel.y + size.y / 2 - 0.5;
								if (voxel.blkId === '3X') {
									px = voxel.x - size.x / 2 + 2.5 + 2 * fixPos;
								}
							} else if (voxel.rotateY === 90) {
								px = voxel.x - size.x / 2 + 1.5 + fixPos;
								pz = -voxel.y + size.y / 2 + 0.5 + fixPos;
							} else if (voxel.rotateY === -90) {
								px = voxel.x - size.x / 2 + 0.5 + fixPos;
								pz = -voxel.y + size.y / 2 - 0.5;
							}
						}
						if (voxel.rotateZ !== 0) {
							matrix.multiply(new Matrix4().makeRotationZ(voxel.rotateZ * Math.PI / 180));
						}
						matrix.setPosition(px, py, pz);
						im.setMatrixAt(i, matrix);
						if (faded) {
							color.setHex(0xB3D7D1);
						} else {
							const hex = palette[voxel.c];
							color.setRGB(
								(hex >> 0 & 0xff) / 0xff,
								(hex >> 8 & 0xff) / 0xff,
								(hex >> 16 & 0xff) / 0xff
							);
						}
						im.setColorAt(i, color);
					}
					im.instanceMatrix && (im.instanceMatrix.needsUpdate = true);
					im.instanceColor && (im.instanceColor.needsUpdate = true);
					this.add(im);
					return im;
				};
				const normal = [];
				const faded = [];
				for (let i = 0; i < groupVoxels.length; i++) {
					const voxel = groupVoxels[i];
					const isCurrentLayer = voxel.z === currentZ;
					if (showFullModel || isCurrentLayer || size.z <= currentZ || keepOrginColor) normal.push(voxel);
					else faded.push(voxel);
				}
				buildInstanced(normal, false);
				buildInstanced(faded, true);

				// 边线处理
				// 当currentZ>=0且currentZ<最大层数时（查看图纸），无论edgeType如何设置，当前层都保持绿色边线
				const isViewingBlueprint = currentZ >= 0 && currentZ < size.z - 1;
				const shouldShowEdges = edgeType !== 'none' || isViewingBlueprint;
				if (shouldShowEdges) {
					const edgeGeo = new EdgesGeometry(groupBaseMesh.geometry, 15);

					// 获取体素颜色的辅助函数
					const getVoxelColor = (voxel) => {
						const isCurrentLayer = voxel.z === currentZ;
						if (showFullModel || isCurrentLayer || size.z <= currentZ || keepOrginColor) {
							// 完整模型或当前层使用原始颜色
							const hex = palette[voxel.c];
							return new Color(
								(hex >> 0 & 0xff) / 0xff,
								(hex >> 8 & 0xff) / 0xff,
								(hex >> 16 & 0xff) / 0xff
							);
						} else {
							// currentZ层以下的体素使用#B3D7D1颜色
							return new Color(0xB3D7D1);
						}
					};

					// 获取边线颜色的函数
					const getEdgeColor = (voxel) => {
						const isCurrentLayer = voxel.z === currentZ;
						if (isCurrentLayer && highLight) {
							// 当前层始终显示绿色边线
							return new Color(0x00ff00);
						} else {
							// currentZ层以下的体素根据edgeType设置边线颜色
							switch (edgeType) {
								case 'dark':
									return new Color(0x333333); // 深色边线
								case 'natural':
									// 自然边线：体素颜色稍微深一点
									const voxelColor = getVoxelColor(voxel);
									const darkerColor = new Color();
									darkerColor.copy(voxelColor);
									darkerColor.multiplyScalar(0.85); // 使颜色变深15%
									return darkerColor;
								case 'none':
									// 当edgeType为none但处于查看图纸模式时，其他层不显示边线
									return null;
								default:
									return new Color(0x333333);
							}
						}
					};

					for (let i = 0; i < groupVoxels.length; i++) {
						const voxel = groupVoxels[i];
						let px = voxel.x - size.x / 2 + 0.5;
						let py = voxel.z - size.z / 2 + 0.5;
						let pz = -voxel.y + size.y / 2 + 0.5;

						const edgeColor = getEdgeColor(voxel);
						
						// 如果边线颜色为null，跳过这个体素的边线
						if (edgeColor === null) continue;
						
						const isCurrentLayerEdge = voxel.z === currentZ;
						const fadedEdge = !(showFullModel || isCurrentLayerEdge || size.z <= currentZ || keepOrginColor);
						const edgeMaterial = new LineBasicMaterial({ 
							color: edgeColor, 
							depthWrite: false, 
							depthTest: true,
							transparent: fadedEdge,
							opacity: fadedEdge ? VOXEL_OPACITY : 1.0
						});

						const edge = new LineSegments(edgeGeo, edgeMaterial);
						edge.position.set(px, py, pz);
						edge.scale.set(groupScale + 0.000025, groupScale + 0.000025, groupScale + 0.000025); // 略大于本体，边线更粗
						
						// 应用旋转 - 旋转会围绕体素中心进行
						if (voxel.rotateX !== 0) {
							edge.rotation.x = voxel.rotateX * Math.PI / 180;
						}
						if (voxel.rotateY !== 0) {
							if (voxel.blkId === '4X' && voxel.direction === 'Y'){
								// Y轴合并的需要旋转90度
								edge.rotation.y = 90 * Math.PI / 180;
							} else {
								edge.rotation.y = voxel.rotateY * Math.PI / 180;
							}
							if (voxel.rotateY === 360) {
								pz = -voxel.y + size.y / 2 + 0.5 + fixPos;
								if (voxel.blkId === '4X' && voxel.direction === 'Y') {
									// Y轴合并的需要移位
									px = voxel.x - size.x / 2 + 1.5 + 2 * fixPos;
								}
							} else if (voxel.rotateY === 270) {
								px = voxel.x - size.x / 2 + 0.5 + fixPos;
								pz = -voxel.y + size.y / 2 - 0.5 - fixPos;
								if (voxel.blkId === '3X') {
									pz = -voxel.y + size.y / 2 - 1.5 - fixPos;
								}
							} else if (voxel.rotateY === 180) {
								px = voxel.x - size.x / 2 + 1.5 + 2 * fixPos;
								pz = -voxel.y + size.y / 2 - 0.5;
								if (voxel.blkId === '3X') {
									px = voxel.x - size.x / 2 + 2.5 + 2 * fixPos;
								}
							} else if (voxel.rotateY === 90) {
								px = voxel.x - size.x / 2 + 1.5 + fixPos;
								pz = -voxel.y + size.y / 2 + 0.5 + fixPos;
							} else if (voxel.rotateY === -90) {
								px = voxel.x - size.x / 2 + 0.5 + fixPos;
								pz = -voxel.y + size.y / 2 - 0.5;
							}
							edge.position.set(px, py, pz);
							edge.scale.set(groupScale + 0.000025, groupScale + 0.000025, groupScale + 0.000025); // 略大于本体，边线更粗
						}
						if (voxel.rotateZ !== 0) {
							edge.rotation.z = voxel.rotateZ * Math.PI / 180;
						}
						
						edge.renderOrder = 1; // 保证边线后渲染
						this.add(edge);
					}
				}
			}
		};

		// 获取颗粒统计信息的方法
		this.getPartStats = () => {
			return this.partStats;
		};

		// 获取全部的颗粒统计信息
		this.getTotalPartStats = () => {
			if (!BLKData || !BLKData.data) {
				return { total: 0, parts: [] };
			}
			const totalStats = {
				total: 0,
				parts: []
			};
			const partCounts = {};
			const partColorCounts = {};
			// 遍历 BLKData.data
			for (const [key, voxelData] of Object.entries(BLKData.data)) {
				const [c, adjacentCount, blkId, rotX, rotY, rotZ, show] = voxelData;
				if (!show || !blkId) continue;
				// 统计类型
				if (!partCounts[blkId]) {
					partCounts[blkId] = 0;
					partColorCounts[blkId] = {};
				}
				partCounts[blkId]++;
				// 统计颜色
				const color = getRenderColor(c);
				if (!partColorCounts[blkId][color]) {
					partColorCounts[blkId][color] = 0;
				}
				partColorCounts[blkId][color]++;
				totalStats.total++;
			}
			for (const [blkId, count] of Object.entries(partCounts)) {
				const colors = partColorCounts[blkId];
				totalStats.parts.push({
					type: blkId,
					count: count,
					color: colors
				});
			}
			return totalStats;
		};
		
		// 获取全部的颜色统计信息
		this.getTotalColorStats = () => {
			if (!palette || !Array.isArray(palette)) {
				return { total: 0, colors: [] };
			}
			
			const colorStats = {
				total: 0,
				colors: []
			};
			
			// 统计每个颜色索引的使用次数
			const colorIndexCounts = {};
			
			// 遍历原始体素数据，统计颜色索引的使用次数
			for (let j = 0; j < data.length; j += 4) {
				const colorIndex = data[j + 3];
				if (colorIndex !== undefined && colorIndex !== null && colorIndex >= 0 && colorIndex < palette.length) {
					colorIndexCounts[colorIndex] = (colorIndexCounts[colorIndex] || 0) + 1;
				}
			}
			
			// 将颜色索引转换为实际颜色并合并统计
			const colorCounts = {};
			for (const [colorIndex, count] of Object.entries(colorIndexCounts)) {
				const actualColor = getRenderColor(parseInt(colorIndex));
				colorCounts[actualColor] = (colorCounts[actualColor] || 0) + count;
			}
			
			// 转换为数组格式
			for (const [color, count] of Object.entries(colorCounts)) {
				colorStats.colors.push({
					color: color,
					count: count
				});
				colorStats.total += count;
			}
			
			// 按数量降序排序
			colorStats.colors.sort((a, b) => b.count - a.count);
			
			return colorStats;
		};
		// 获取指定层的颗粒统计信息
		this.getLayerPartStats = (layerZ) => {
			if (!BLKData || !BLKData.data) {
				return { total: 0, parts: [] };
			}
			
			const layerStats = {
				total: 0,
				parts: []
			};
			
			const layerPartCounts = {};
			const layerPartColorCounts = {};
			
			// 遍历原始体素数据来获取正确的颜色信息
			for (let j = 0; j < data.length; j += 4) {
				const x = data[j + 0];
				const y = data[j + 1];
				const z = data[j + 2];
				const colorIndex = data[j + 3]; // 使用原始体素数据的颜色索引
				
				// 检查是否在指定层
				if (z === layerZ) {
					// 从BLKData中获取blkId信息
					const key = `${x},${y},${z}`;
					const voxelData = BLKData.data[key];
					
					// 如果BLKData中没有对应数据，使用默认值
					let blkId = null;
					let show = 0;
					
					if (voxelData) {
						const [c, adjacentCount, voxelBlkId, rotX, rotY, rotZ, voxelShow] = voxelData;
						blkId = voxelBlkId;
						show = voxelShow;
					}
					
					// 如果show为false，跳过这个体素
					if (!show || !blkId) continue;
					
					// 使用blkId进行统计
					if (!layerPartCounts[blkId]) {
						layerPartCounts[blkId] = 0;
						layerPartColorCounts[blkId] = {};
					}
					layerPartCounts[blkId]++;
					
					// 统计颜色 - 使用与渲染代码一致的颜色处理逻辑
					const color = getRenderColor(colorIndex);
					if (!layerPartColorCounts[blkId][color]) {
						layerPartColorCounts[blkId][color] = 0;
					}
					layerPartColorCounts[blkId][color]++;
					
					layerStats.total++;
				}
			}
			
			for (const [blkId, count] of Object.entries(layerPartCounts)) {
				const colors = layerPartColorCounts[blkId];
				layerStats.parts.push({
					type: blkId, // 使用blkId而不是partName
					count: count,
					color: colors
				});
			}
			
			return layerStats;
		};

		// 加载积木颗粒 
		function loadObjModels(type = 'blk01', cb) {
			const names = ['0', '1', '1T', '2', '2L', '2X', '3', '3X', '4', '4X'];
			const loader = new OBJLoader();
			let loadCount = 0;
			objModels = {};
			names.forEach(name => {
				const key = `${type}:${name}`;
				let promise = OBJ_MODEL_CACHE.get(key);
				if (!promise) {
					const modelPath = `${cdnHost}obj/${type}/${name}.obj`;
					promise = new Promise((resolve, reject) => {
						loader.load(modelPath, resolve, undefined, reject);
					});
					OBJ_MODEL_CACHE.set(key, promise);
				}
				promise.then((obj) => {
					objModels[name] = obj;
					loadCount++;
					if (loadCount >= names.length) {
						cb(objModels);
					}
				});
			});
		}

		if (objModels) {
			processObj();
		} else {
			loadObjModels(modelType, processObj)
		}

	}

}

export { BLKMesh };
